// src/lib/materialLabel.js
// 🏷️ Etiqueta identificadora del MATERIAL (plancha/stock completo) — ESPEJO de
// la etiqueta de retal (remnantLabel.js) sobre el MISMO motor `label.js`.
//
// Layout pedido en campo: SOLAPA EN BLANCO arriba (línea punteada) para pegar
// la etiqueta o meterla entre las hojas de la estiba; el logo y TODA la info
// van debajo de la línea. La ubicación pasa por pdfText() (la flecha "→" no
// existe en la fuente del PDF y salía como basura) y se envuelve, no se trunca.
import { newLabelDoc, drawQr, pdfText } from "./label";
import { t } from "./i18n";

const FLAP_PT = 90; // ~1.25 in de solapa en blanco

/**
 * Construye (sin guardar) el PDF de la etiqueta de un material. Devuelve el doc.
 * @param {object} args
 * @param {object} args.material       doc de `materials` (uniqueCode, name, category, …)
 * @param {string} [args.locationText] ubicación del stock YA RESUELTA
 */
export async function renderMaterialLabel({ material, locationText }) {
  const m = material || {};
  const { doc, W, H, M, center, bodyY } = await newLabelDoc({ flap: FLAP_PT });

  let y = bodyY + 14;

  // Jerarquía pedida en campo: NOMBRE + MEDIDA + ESPESOR mandan; el código va discreto.
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(t("label.material.tag"), center, y, { align: "center" });
  y += 22;

  // NOMBRE grande con AUTO-SIZE (A1): el displayName puede ser largo (se desbordaba/cortaba,
  // ver MAT-00053). Bajamos el tamaño de 20 hasta que el texto QUEPA en ≤2 líneas (mín. 11);
  // así nada se trunca y el título se ajusta al ancho.
  const nameText = pdfText(String(m.displayName || m.name || "—").toUpperCase());
  doc.setFont("helvetica", "bold");
  let nameFs = 20; let nameLines;
  for (; nameFs >= 11; nameFs -= 1) {
    doc.setFontSize(nameFs);
    nameLines = doc.splitTextToSize(nameText, W - 2 * M);
    if (nameLines.length <= 2) break;
  }
  nameLines = nameLines.slice(0, 2); // red de seguridad (mín. alcanzado)
  doc.setFontSize(nameFs);
  doc.text(nameLines, center, y, { align: "center" });
  y += nameFs + (nameLines.length - 1) * nameFs;

  // MEDIDA × MEDIDA + ESPESOR juntos, grandes — lo que el piso lee para elegir plancha.
  const dims = (m.dimensions?.width && m.dimensions?.height)
    ? `${m.dimensions.width} x ${m.dimensions.height} ${m.dimensions.unit || "in"}` : null;
  const th = m.thickness?.value ? `${m.thickness.value} ${m.thickness.unit || "mm"}` : null;
  if (dims || th) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(17);
    doc.text(pdfText([dims, th].filter(Boolean).join("  ·  ")), center, y, { align: "center" });
    y += 16;
  }

  // Categoría · color (medio).
  const sub = [m.category, (m.attributes?.color || m.color)].filter(Boolean).join("  ·  ");
  if (sub) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text(doc.splitTextToSize(pdfText(sub), W - 2 * M), center, y, { align: "center" });
    y += 14;
  }

  // Código DISCRETO (canónico; el legacy entre paréntesis durante la transición).
  const code = m.code || m.uniqueCode || "—";
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  const codeLine = (m.code && m.legacyCode) ? `${code}  (${m.legacyCode})` : code;
  doc.text(pdfText(codeLine), center, y, { align: "center" });
  doc.setTextColor(0);
  y += 8;

  // QR (apunta al material) — el escáner resuelve el material.
  const payload = JSON.stringify({ t: "material", materialId: m.id || null, code: m.code || m.uniqueCode || null });
  const qrSize = 96;
  if (await drawQr(doc, payload, center - qrSize / 2, y + 4, qrSize)) y += qrSize + 14; else y += 10;

  // Footer: existencia + ubicación (envuelta, saneada) + fecha (bilingüe).
  doc.setDrawColor(200); doc.setLineWidth(0.75); doc.line(M, y - 4, W - M, y - 4);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold"); doc.text(`${t("label.stock")}:`, M, y + 8);
  doc.setFont("helvetica", "normal"); doc.text(String(m.stock?.quantity ?? "—"), M + 90, y + 8);
  y += 22;
  const locLines = doc.splitTextToSize(pdfText(locationText || m.stock?.location || "—"), W - M - 90).slice(0, 2);
  doc.setFont("helvetica", "bold"); doc.text(`${t("label.location")}:`, M, y);
  doc.setFont("helvetica", "normal"); doc.text(locLines, M + 90, y);
  y += 14 + (locLines.length - 1) * 12;
  doc.setFont("helvetica", "bold"); doc.text(`${t("label.date")}:`, M, Math.min(y, H - M));
  doc.setFont("helvetica", "normal"); doc.text(new Date().toLocaleDateString(), M + 90, Math.min(y, H - M));

  return doc;
}

/** Genera y DESCARGA la etiqueta del material (desde la tarjeta y tras +Inventariar). */
export async function buildMaterialLabelPdf(args) {
  const doc = await renderMaterialLabel(args);
  doc.save(`LABEL_${args.material?.code || args.material?.uniqueCode || "MATERIAL"}.pdf`);
}
